<template>
  <div>
    <HeadSection
      :title="categoryName"
      permissionOfSection="fortune_wheel"
      :breadcrumbs="breadcrumbs"
    >
      <template #actions>
        <a-col :span="14" class="d-flex justify-content-end" style="gap: 10px;">
          <a-button
            type="outline"
            size="large"
            :disabled="loading"
            @click="toggleShowPromocodeModal(true, { title: 'Edit Promo Code category', mode: 'edit' })"
          >
            <div class="d-flex align-items-center justify-content-center">
              <a-icon type="setting" class="font-size-20 text-primary" />
            </div>
          </a-button>
          <a-button
            type="primary"
            size="large"
            :disabled="loading"
            @click="toggleShowPromocodeModal(true, { title: 'Promo Code adding', mode: 'add' })"
          >
            Add promo code
          </a-button>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="userLoading" permissionOfSection="fortune_wheel" :isBlockStyle="true">
      <div class="d-flex flex-column">
        <div class="d-flex flex-wrap mb-5" style="gap: 30px;">
          <ContentBlock style="min-width: 300px">
            <template #title>
              <div class="d-flex justify-content-between">
                <span class="d-block font-weight-bold font-size-18">Total Promo Codes</span>
                <a-icon
                  type="tags"
                  theme="filled"
                  class="font-size-30"
                />
              </div>
            </template>
            <template #content>
              <Skeleton v-if="!categoryStats" width="80px" height="40px" />
              <span v-else class="d-block font-weight-bold font-size-40">
                {{ categoryStats ? categoryStats.total : '—' }}
              </span>
            </template>
          </ContentBlock>
          <ContentBlock style="min-width: 300px">
            <template #title>
              <div class="d-flex justify-content-between">
                <span class="d-block font-weight-bold font-size-18">Used Promo Codes</span>
                <a-icon
                  type="check-circle"
                  theme="filled"
                  class="font-size-30 text-success"
                />
              </div>
            </template>
            <template #content>
              <Skeleton v-if="!categoryStats" width="80px" height="40px" />
              <span v-else class="d-block font-weight-bold font-size-40">
                {{ categoryStats ? categoryStats.total_used : '—' }}
              </span>
            </template>
          </ContentBlock>
          <ContentBlock style="min-width: 300px">
            <template #title>
              <div class="d-flex justify-content-between">
                <span class="d-block font-weight-bold font-size-18">Unused Promo Codes</span>
                <a-icon
                  type="minus-circle"
                  theme="filled"
                  class="font-size-30 text-primary"
                />
              </div>
            </template>
            <template #content>
              <Skeleton v-if="!categoryStats" width="80px" height="40px" />
              <span v-else class="d-block font-weight-bold font-size-40">
                {{ categoryStats ? categoryStats.total_unused : '—' }}
              </span>
            </template>
          </ContentBlock>
        </div>
        <PromocodesTable ref="promocodes-table" @update="getCategoryStats" />
      </div>
    </MainContainer>
    <PromocodeModal
      v-if="showPromocodeModal"
      :show="showPromocodeModal"
      :title="promocodeModalSettings.title"
      :mode="promocodeModalSettings.mode"
      :categoryName="categoryName"
      actionButtonName="Save"
      @edit="editCategoryName"
      @add="addedPromocodes"
      @close="toggleShowPromocodeModal"
    />
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import ContentBlock from '@/components/main/contentBlock'
import Skeleton from '@/components/custom/skeleton/Skeleton.vue'
import PromocodesTable from '@/views/apps/fortune-wheel/promocodes/components/PromocodesTable.vue'
import PromocodeModal from '@/views/apps/fortune-wheel/promocodes/components/PromocodeModal.vue'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'

const wheelApiUrl = process.env.VUE_APP_WHEEL_API_URL

export default {
  props: {
    editPermission: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HeadSection,
    MainContainer,
    ContentBlock,
    Skeleton,
    PromocodesTable,
    PromocodeModal,
  },
  data: () => ({
    categoryId: null,
    categoryName: null,
    categoryStats: null,
    promocodeModalSettings: {
      title: '',
      mode: '',
    },
    showPromocodeModal: false,
    loading: false,
  }),
  computed: {
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    breadcrumbs() {
      return [
        { name: 'Promo Code Categories', link: '/fortune-wheel/promo-codes' },
        { name: this.categoryName || '', link: undefined },
      ]
    },
  },
  methods: {
    toggleShowPromocodeModal(status = false, settings = { title: '', mode: '' }) {
      this.promocodeModalSettings = settings
      this.showPromocodeModal = status
    },
    editCategoryName(newCategoryName) {
      this.categoryName = newCategoryName
    },
    addedPromocodes() {
      this.getCategoryStats()
      this.$refs['promocodes-table'].fetchPromocodesList()
    },
    async getCategoryName() {
      try {
        const url = `${wheelApiUrl}/admin/promocodes/categories/${this.categoryId}`

        const response = await apiClient.get(url)
        if (!response?.data?.status) {
          getErrorMessage(response?.data)
          return
        }
        this.categoryName = response?.data?.data?.name
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      }
    },
    async getCategoryStats() {
      try {
        const url = `${wheelApiUrl}/admin/promocodes/categories/${this.categoryId}/get-stats`

        const response = await apiClient.get(url)
        if (!response?.data?.status) {
          getErrorMessage(response?.data)
          return
        }
        this.categoryStats = response?.data?.data
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      }
    },
  },
  async mounted() {
    this.categoryId = this.$route?.params?.id
    try {
      this.loading = true
      await Promise.all([this.getCategoryName(), this.getCategoryStats()])
    } finally {
      this.loading = false
    }
  },
}
</script>
